import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../utils';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* ── Backdrop ── */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[3px] animate-fade-in"
        onClick={onClose}
      />

      {/* ── Panel ── */}
      <div
        ref={contentRef}
        className={cn(
          'relative w-full flex flex-col rounded-2xl max-h-[88vh]',
          'bg-surface dark:bg-dark-800',
          'border border-line-subtle dark:border-dark-600/80',
          'shadow-[0_24px_64px_-8px_rgba(0,0,0,0.35),0_0_0_1px_rgba(0,0,0,0.05)]',
          'dark:shadow-[0_32px_80px_-8px_rgba(0,0,0,0.7),0_0_0_1px_rgba(99,102,241,0.15)]',
          'animate-slide-in-up',
          sizes[size],
        )}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-line-subtle dark:border-dark-700 shrink-0">
            <h2 id="modal-title" className="text-sm font-bold text-content dark:text-white tracking-tight">
              {title}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-7 h-7 flex items-center justify-center rounded-lg
                text-content-subtle dark:text-dark-500
                hover:bg-gray-100 dark:hover:bg-dark-700
                hover:text-gray-700 dark:hover:text-dark-200
                transition-colors duration-150"
            >
              <X className="size-4" />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 min-h-0">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-4 border-t border-line-subtle dark:border-dark-700 flex justify-end gap-2 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
